import useAsync from 'hooks/useAsync';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { useNavigate } from 'react-router-dom';
import PageLoader from 'components/PageLoader';
import { KnowledgeTestAttempt } from 'types/api/TestAttempt';
import { useCallback, useEffect, useState } from 'react';
import { getKnowledgeTestAttempt } from 'actions/knowledgeTests';
import KnowledgeTestQuestions from './KnowledgeTestQuestions';
import Instructions from 'components/TestAttempts/Instructions';
import HonorCodeAgreement from 'components/TestAttempts/HonorCodeAgreement';
import { startAttempt } from 'actions/testAttempts';
import { KnowledgeTestProvider } from './KnowledgeTestContext';
import { default as Calibrations } from 'pages/TestPreparations';
import { getTestPreparations } from 'actions/testPreparations';
import useCurrentUser from 'hooks/useCurrentUser';

interface KnowledgeTestProps {
  id: number;
  testType?: string;
}

function KnowledgeTest({ id, testType }: KnowledgeTestProps) {
  const { data, run, isPending } = useAsync<KnowledgeTestAttempt>();
  const [preTestState, setPreTestState] = useState(
    () => window.localStorage.getItem(`pre_test_state_${id}`) || 'instructions'
  );
  const currentUser = useCurrentUser();

  const navigate = useNavigate();

  useEffect(() => {
    if (testType === 'calibration' || preTestState === 'calibration_test') {
      run(getTestPreparations(Number(currentUser.id), Number(id)));
    } else {
      run(getKnowledgeTestAttempt(Number(id)));
    }
  }, [run, id, testType, currentUser.id, preTestState]);

  const handlePreTestState = useCallback(
    (state: string) => {
      window.localStorage.setItem(`pre_test_state_${id}`, state);
      setPreTestState(state);
    },
    [setPreTestState, id]
  );

  if (isPending) {
    return <PageLoader />;
  }

  if (data?.completed_at) {
    if (testType === 'calibration') {
      navigate(
        RouteHelpers.getPath('calibrations-score-report', {
          id: id
        })
      );
    } else {
      navigate(
        RouteHelpers.getPath('test-attempts-score-report', {
          id: id
        })
      );
    }
    return null;
  }

  if (
    data?.user_test?.terms_accepted_at ||
    preTestState === 'knowledge_test' ||
    preTestState === 'calibration_test'
  ) {
    if (!data.started_at && preTestState !== 'calibration_test') {
      run(startAttempt(Number(id)));
    }

    if (preTestState === 'knowledge_test' || preTestState === 'resources') {
      return (
        <KnowledgeTestProvider id={id}>
          <KnowledgeTestQuestions {...data} />
        </KnowledgeTestProvider>
      );
    }
  }

  if (preTestState === 'calibration_test' && data?.terms_accepted_at !== null) {
    // Reusing TestPreparations component for calibrations
    return <Calibrations testType="calibrations" />;
  }

  if (preTestState === 'instructions' || preTestState === 'resources') {
    return (
      <Instructions
        title={data ? data.title : i18n.ft(messages.calibrationTitle)}
        handlePreTestState={handlePreTestState}
        testType={
          testType === 'calibration'
            ? 'calibration'
            : data.test_template.test_type
        }
      />
    );
  }

  return (
    <HonorCodeAgreement
      title={data ? data.title : i18n.ft(messages.calibrationTitle)}
      userTestId={
        testType === 'calibration'
          ? id.toString()
          : data.user_test.id.toString()
      }
      testAttemptId={id}
      handlePreTestState={handlePreTestState}
      testType={testType === 'calibration' ? 'calibration' : 'knowledge'}
    />
  );
}

export default KnowledgeTest;
